import { daysOfTheWeek } from "@plasma/data/time";
import {
  formatAddress,
  hasRequiredAddressFields,
} from "@plasma/utils/format-address";
import { applyPhoneNumberMask } from "@plasma/utils/phone-numbers";
import { formatDate } from "@plasma/utils/time";
import { useNavigate } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import type React from "react";
import { twMerge } from "tailwind-merge";

import type { LocationById } from "~/api-refs";
import { enumIncludes, parseEnumValue } from "~/lib/parse-enum-value";

// V4.1: Route-based profile blocks for URL context tracking
export const LocationProfileBlocks = ({
  location,
  locationId,
}: {
  location: LocationById;
  locationId: string;
}) => {
  const navigate = useNavigate();

  // V4.1: Navigate to route-based edit modal
  const handleEditField = (field: string) => {
    void navigate({
      to: "/locations/$id/profile/edit/$field",
      params: { id: locationId, field },
    });
  };

  return (
    <div>
      <LocationProfileBlock
        className="items-center py-3"
        fieldKey="logo"
        label="Logo"
        onEdit={handleEditField}
      >
        <img
          alt={`${location.name} logo`}
          className="size-9 rounded-full"
          height={36}
          src={location.logoUrl ?? "/images/location-logo-placeholder.svg"}
          width={36}
        />
      </LocationProfileBlock>
      <LocationProfileBlock
        fieldKey="name"
        label="Name"
        onEdit={handleEditField}
      >
        {location.name}
      </LocationProfileBlock>
      <LocationProfileBlock
        fieldKey="address"
        label="Address"
        onEdit={handleEditField}
      >
        {location.address && hasRequiredAddressFields(location.address)
          ? formatAddress(location.address)
          : "Address not set"}
      </LocationProfileBlock>
      <LocationProfileBlock
        fieldKey="phoneNumber"
        label="Phone Number"
        onEdit={handleEditField}
      >
        {location.phoneNumber
          ? applyPhoneNumberMask(location.phoneNumber)
          : "N/A"}
      </LocationProfileBlock>
      <LocationProfileBlock
        className="py-4"
        fieldKey="businessHours"
        label="Business hours"
        onEdit={handleEditField}
      >
        <div className="grid gap-2">
          <LocationOperationStatus
            closeDateRange={{
              closeStartDate: location.closeStartDate ?? null,
              closeEndDate: location.closeEndDate ?? null,
            }}
            operationalStatus={parseEnumValue(
              location.operationalStatus,
              enumIncludes([
                "OPEN",
                "TEMPORARILY_CLOSED",
                "PERMANENTLY_CLOSED",
              ] as const),
              "OPEN"
            )}
          />
          {location.operationalStatus !== "PERMANENTLY_CLOSED" &&
            daysOfTheWeek.map((day) => {
              const businessHour = location.businessHours.filter(
                (h) => h.dayOfTheWeek === day
              );
              return (
                <div className="text-muted-foreground flex gap-2" key={day}>
                  <p className="w-28">{day}</p>
                  <p>
                    {businessHour[0]?.isOpen
                      ? businessHour
                          .map(
                            (h) => `${h.openTime ?? ""} - ${h.closeTime ?? ""}`
                          )
                          .join(", ")
                      : "Closed"}
                  </p>
                </div>
              );
            })}
        </div>
      </LocationProfileBlock>
      <LocationProfileBlock
        fieldKey="styleAndType"
        label="Style & Type"
        onEdit={handleEditField}
      >
        {location.style}, {location.type}
      </LocationProfileBlock>
    </div>
  );
};

// V4.1: Simplified block that navigates to route-based modal
const LocationProfileBlock = ({
  label,
  fieldKey,
  className,
  children,
  onEdit,
}: {
  label: string;
  fieldKey: string;
  className?: string;
  children: React.ReactNode;
  onEdit: (field: string) => void;
}) => {
  // Remove special characters from label to use as id
  const id = label
    .replace(" ", "-")
    .toLowerCase()
    .replace(/[^a-zA-Z0-9-]/g, "");

  return (
    // biome-ignore lint/a11y/useSemanticElements: This is a clickable row that needs div semantics for styling; it has proper keyboard handling and role
    <div
      className={twMerge(
        "body-sm border-border text-foreground flex cursor-pointer gap-2 border-t py-5",
        className
      )}
      id={id}
      onClick={() => onEdit(fieldKey)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit(fieldKey);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <p className="body text-foreground text-weight-medium w-40">{label}</p>
      {children}
      <div className="flex-1" />
      <div className="flex justify-start">
        <ChevronRight className="size-5" />
      </div>
    </div>
  );
};

interface LocationOperationStatusProps {
  operationalStatus: "OPEN" | "TEMPORARILY_CLOSED" | "PERMANENTLY_CLOSED";
  closeDateRange: {
    closeStartDate: string | null;
    closeEndDate: string | null;
  };
}

const LocationOperationStatus = ({
  operationalStatus,
  closeDateRange,
}: LocationOperationStatusProps) => {
  const { closeStartDate, closeEndDate } = closeDateRange;

  if (operationalStatus === "OPEN") {
    return null;
  }

  if (operationalStatus === "PERMANENTLY_CLOSED") {
    return <p className="text-destructive">Closed</p>;
  }

  let statusText = "Until further notice";
  if (closeStartDate && closeEndDate) {
    const startDate = new Date(closeStartDate);
    const endDate = new Date(closeEndDate);

    if (startDate.getFullYear() === endDate.getFullYear()) {
      statusText = `${formatDate(startDate, "MMMM D")} - ${formatDate(endDate, "MMMM D, YYYY")}`;
    } else {
      statusText = `${formatDate(startDate, "MMMM D, YYYY")} - ${formatDate(endDate, "MMMM D, YYYY")}`;
    }
  } else if (closeEndDate) {
    statusText = `Until ${formatDate(closeEndDate, "MMMM D, YYYY")}`;
  } else if (closeStartDate) {
    statusText = `From ${formatDate(closeStartDate, "MMMM D, YYYY")}`;
  }

  return (
    <div className="text-destructive flex gap-2">
      <p className="w-28">Closed</p>
      <p>{statusText}</p>
    </div>
  );
};
